import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  ButtonGroup,
  Button,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Block as ExcludeIcon,
  SwapHoriz as SwapIcon,
  MoreVert as MoreIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import RecommendationScoreBar from './RecommendationScoreBar';
import ScoreBreakdownPopover from './ScoreBreakdownPopover';
import OverrideBadge from './OverrideBadge';
import { RELATION_COLORS, RELATION_LABELS } from './FilterControls';
import { OVERRIDE_DECISIONS } from './hooks/useOverrides';
import { getCanonicalTypeDisplayName } from '../../utils/canonicalTypes';
import { RAIL_WIDTH, V2, hoverOnly } from '../../theme/tokens'
import { formatNumber } from '../../utils/format'

/**
 * Card displaying a single recommendation with actions.
 */
export default function RecommendationCard({
  recommendation,
  sourceProductId,
  onOverride,
  onOpenOverrideDialog,
  compact = false,
}) {
  const {
    target_product_id,
    product_name,
    vendor,
    product_type,
    canonical_type,
    role,
    image_url,
    price,
    score,
    relation_type,
    override,
  } = recommendation;

  // Use canonical_type if available, fallback to product_type, and convert to display name
  const displayType = getCanonicalTypeDisplayName(canonical_type || product_type);

  const handleQuickAction = (decision) => {
    if (onOverride) {
      // If clicking the same decision, clear it (toggle behavior)
      const newDecision = override?.decision === decision ? OVERRIDE_DECISIONS.AUTO : decision;
      onOverride({
        source_product_id: sourceProductId,
        target_product_id,
        decision: newDecision,
        relation_type,
      });
    }
  };

  const currentDecision = override?.decision;
  const isApproved = currentDecision === OVERRIDE_DECISIONS.APPROVE;
  const isExcluded = currentDecision === OVERRIDE_DECISIONS.EXCLUDE;
  const isDecided = isApproved || isExcluded || currentDecision?.startsWith('force_');
  const relationColor = RELATION_COLORS[relation_type] || V2.ink45;

  if (compact) {
    return (
      <CompactRecommendationCard
        recommendation={recommendation}
        sourceProductId={sourceProductId}
        onOverride={onOverride}
        onOpenOverrideDialog={onOpenOverrideDialog}
      />
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        opacity: isExcluded ? 0.5 : 1,
        transition: 'opacity 0.2s, box-shadow 0.2s, border-color 0.2s',
        borderColor: isApproved ? 'success.main' : isExcluded ? 'error.light' : undefined,
        borderWidth: isDecided ? 2 : 1,
        bgcolor: isDecided ? (isApproved ? 'success.50' : isExcluded ? 'error.50' : 'action.hover') : undefined,
        // K-5 (Welle 2): reine Wirkung (Schatten) ohne Hintergrund.
        ...hoverOnly({ boxShadow: 2 }),
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Product Image */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'action.hover',
              flexShrink: 0,
            }}
          >
            {image_url ? (
              <img
                src={image_url}
                alt={product_name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.disabled',
                  fontSize: 12,
                }}
              >
                No image
              </Box>
            )}
          </Box>

          {/* Product Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  flex: 1,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {product_name}
              </Typography>
              {override && <OverrideBadge override={override} />}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {vendor}
              </Typography>
              {displayType && (
                <>
                  <Typography variant="caption" color="text.disabled">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {displayType}
                    {role && ` (${role})`}
                  </Typography>
                </>
              )}
              {price && (
                <>
                  <Typography variant="caption" color="text.disabled">•</Typography>
                  <Typography variant="caption" fontWeight={500}>
                    ${formatNumber(parseFloat(price))}
                  </Typography>
                </>
              )}
            </Stack>

            {/* Score Bar with Breakdown Popover */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip
                label={RELATION_LABELS[relation_type] || relation_type}
                size="small"
                sx={{
                  bgcolor: `${relationColor}20`,
                  color: relationColor,
                  fontWeight: 500,
                  fontSize: 11,
                }}
              />
              <ScoreBreakdownPopover
                sourceProductId={sourceProductId}
                targetProductId={target_product_id}
                relationType={relation_type}
              >
                <RecommendationScoreBar
                  score={score}
                  size="medium"
                  sx={{ flex: 1, maxWidth: 200, cursor: 'pointer' }}
                />
              </ScoreBreakdownPopover>
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ButtonGroup size="small">
                <Tooltip title={isApproved ? "Click to undo approval" : "Approve this recommendation"}>
                  <Button
                    color="success"
                    variant={isApproved ? "contained" : "outlined"}
                    onClick={() => handleQuickAction(OVERRIDE_DECISIONS.APPROVE)}
                    sx={{ minWidth: 36 }}
                  >
                    <ApproveIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title={isExcluded ? "Click to undo exclusion" : "Exclude this recommendation"}>
                  <Button
                    color="error"
                    variant={isExcluded ? "contained" : "outlined"}
                    onClick={() => handleQuickAction(OVERRIDE_DECISIONS.EXCLUDE)}
                    sx={{ minWidth: 36 }}
                  >
                    <ExcludeIcon fontSize="small" />
                  </Button>
                </Tooltip>
                <Tooltip title="Change relation type">
                  <Button
                    variant="outlined"
                    onClick={() => onOpenOverrideDialog?.(recommendation)}
                    sx={{ minWidth: 36 }}
                  >
                    <SwapIcon fontSize="small" />
                  </Button>
                </Tooltip>
              </ButtonGroup>

              <Box sx={{ flex: 1 }} />

              <Tooltip title="View product">
                <IconButton
                  size="small"
                  href={`/products/${target_product_id}`}
                  target="_blank"
                >
                  <OpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Compact version of the recommendation card for denser lists.
 */
function CompactRecommendationCard({
  recommendation,
  sourceProductId,
  onOverride,
  onOpenOverrideDialog,
}) {
  const {
    target_product_id,
    product_name,
    vendor,
    image_url,
    score,
    relation_type,
    override,
  } = recommendation;

  const currentDecision = override?.decision;
  const isApproved = currentDecision === OVERRIDE_DECISIONS.APPROVE;
  const isExcluded = currentDecision === OVERRIDE_DECISIONS.EXCLUDE;
  const isDecided = isApproved || isExcluded || currentDecision?.startsWith('force_');
  const relationColor = RELATION_COLORS[relation_type] || V2.ink45;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        borderRadius: 1,
        opacity: isExcluded ? 0.5 : 1,
        bgcolor: isDecided ? (isApproved ? 'success.50' : isExcluded ? 'error.50' : 'action.selected') : undefined,
        borderLeft: isDecided ? `${RAIL_WIDTH}px solid` : undefined,
        borderColor: isApproved ? 'success.main' : isExcluded ? 'error.main' : 'primary.main',
        ...hoverOnly({
          bgcolor: isDecided ? undefined : 'action.hover',
        }),
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'action.hover',
          flexShrink: 0,
        }}
      >
        {image_url && (
          <img
            src={image_url}
            alt={product_name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {product_name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {vendor}
        </Typography>
      </Box>

      {/* Relation Type */}
      <Chip
        label={RELATION_LABELS[relation_type] || relation_type}
        size="small"
        sx={{
          bgcolor: `${relationColor}20`,
          color: relationColor,
          fontWeight: 500,
          fontSize: 10,
          height: 20,
        }}
      />

      {/* Score */}
      <ScoreBreakdownPopover
        sourceProductId={sourceProductId}
        targetProductId={target_product_id}
        relationType={relation_type}
      >
        <RecommendationScoreBar
          score={score}
          size="small"
          sx={{ width: 80, cursor: 'pointer' }}
        />
      </ScoreBreakdownPopover>

      {/* Override Badge */}
      {override && <OverrideBadge override={override} size="small" />}

      {/* Actions */}
      <Tooltip title="More actions">
        <IconButton
          size="small"
          onClick={() => onOpenOverrideDialog?.(recommendation)}
        >
          <MoreIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/**
 * Recommendation card skeleton for loading state.
 */
export function RecommendationCardSkeleton({ compact = false }) {
  if (compact) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              width: '60%',
              height: 14,
              borderRadius: 0.5,
              bgcolor: 'action.hover',
              mb: 0.5,
            }}
          />
          <Box
            sx={{
              width: '40%',
              height: 12,
              borderRadius: 0.5,
              bgcolor: 'action.hover',
            }}
          />
        </Box>
        <Box
          sx={{
            width: 80,
            height: 6,
            borderRadius: 3,
            bgcolor: 'action.hover',
          }}
        />
      </Box>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                width: '70%',
                height: 16,
                borderRadius: 0.5,
                bgcolor: 'action.hover',
                mb: 1,
              }}
            />
            <Box
              sx={{
                width: '50%',
                height: 12,
                borderRadius: 0.5,
                bgcolor: 'action.hover',
                mb: 1.5,
              }}
            />
            <Box
              sx={{
                width: 120,
                height: 10,
                borderRadius: 5,
                bgcolor: 'action.hover',
                mb: 1.5,
              }}
            />
            <Box
              sx={{
                width: 120,
                height: 28,
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
